import { useState, useMemo, useRef, useEffect } from 'react';
import { ChevronsUpDown, User } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { useUsersSearch } from '@/hooks/useUsersSearch';
import { cn } from '@/lib/cn';
import type { UserSummary } from '@/types/spaces';

interface UserPickerProps {
  /** Selected userId, or undefined for "no selection". */
  value: string | undefined;
  onChange: (user: UserSummary) => void;
  /** Hide users that are already members of the active space. */
  excludeUserIds?: Set<string>;
  placeholder?: string;
  disabled?: boolean;
}

function userLabel(u: UserSummary): string {
  const name = [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
  return name ? `${name} (${u.email})` : u.email;
}

/**
 * Combobox over the org user directory. Search is debounced (300 ms) inside
 * `useUsersSearch`. Emits the picked `UserSummary` so the caller doesn't have
 * to round-trip an id back to a label.
 */
export function UserPicker({ value, onChange, excludeUserIds, placeholder = 'Pick a user…', disabled }: UserPickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const { data, isLoading, isFetching } = useUsersSearch(query, open);

  const visible = useMemo<UserSummary[]>(() => {
    const items = data?.items ?? [];
    if (!excludeUserIds || excludeUserIds.size === 0) return items;
    return items.filter((u) => !excludeUserIds.has(u.userId));
  }, [data, excludeUserIds]);

  const selectedLabel = useMemo(() => {
    if (!value) return null;
    const found = visible.find((u) => u.userId === value);
    return found ? userLabel(found) : value;
  }, [value, visible]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="w-full justify-between font-normal text-left h-auto min-h-10 py-2"
        >
          <span className={cn('truncate text-sm', !selectedLabel && 'text-muted-foreground')}>
            {selectedLabel ?? placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[360px] p-0" align="start">
        <div className="p-2 border-b">
          <input
            ref={inputRef}
            type="text"
            placeholder="Search by email or name…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="focus-ring w-full rounded-md border border-input bg-background text-foreground px-2 py-1.5 text-sm placeholder:text-muted-foreground"
          />
        </div>
        <div className="max-h-60 overflow-y-auto p-1">
          {isLoading || (isFetching && visible.length === 0) ? (
            <p className="py-4 text-center text-sm text-muted-foreground">Searching…</p>
          ) : visible.length === 0 ? (
            <p className="py-4 text-center text-sm text-muted-foreground">
              {query ? 'No users match.' : 'No users available.'}
            </p>
          ) : (
            visible.map((u) => (
              <button
                key={u.userId}
                type="button"
                onClick={() => {
                  onChange(u);
                  setOpen(false);
                }}
                className={cn(
                  'flex w-full items-start gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent',
                  u.userId === value && 'bg-accent font-medium',
                )}
              >
                <User className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span className="flex-1 min-w-0">
                  <span className="block truncate text-foreground">
                    {[u.firstName, u.lastName].filter(Boolean).join(' ') || u.email}
                  </span>
                  {(u.firstName || u.lastName) && (
                    <span className="block truncate text-xs text-muted-foreground">{u.email}</span>
                  )}
                </span>
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
